import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button, ThemeToggle } from "../components/ui";
import { useClientView } from "../contexts/ClientViewContext";
import { useClients } from "../hooks/useClients";
import { useSidebar } from "../contexts/SidebarContext";
import CommandPalette from "./CommandPalette";
import {
  ChevronDownIcon,
  EyeIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  UserCircleIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { ChartBarIcon } from "@heroicons/react/24/solid";

export default function Header() {
  const location = useLocation();
  const { currentUser, permissions, logout, loading } = useAuth();
  const { theme } = useTheme();
  const { clientView, enterClientView, exitClientView, isClientMode } = useClientView();
  const { collapsed, toggleCollapsed } = useSidebar();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const clientDropdownRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  // Only fetch clients list for @seytravel.com users
  const isSeyTravelUser = currentUser?.email?.endsWith('@seytravel.com');
  const { data: allClients = [] } = useClients({ enabled: !!isSeyTravelUser });
  const managedClients = allClients.filter((c) => c.consultingStatus);
  const filteredClientsList = managedClients.filter((c) =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setClientDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Touch gesture state
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const swipeDetected = useRef(false);
  const swipeThreshold = 50;
  const verticalThreshold = 100;

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.documentElement.classList.add("overflow-hidden");
    } else {
      document.documentElement.classList.remove("overflow-hidden");
    }
    return () => {
      document.documentElement.classList.remove("overflow-hidden");
    };
  }, [mobileMenuOpen]);

  // Touch event handlers
  useEffect(() => {
    const edgeThreshold = 100;

    const handleTouchStart = (e) => {
      const startX = e.changedTouches[0].screenX;
      if (mobileMenuOpen) {
        if (!e.target.closest(".mobile-menu-sidebar")) {
          touchStartX.current = 0;
          return;
        }
      } else {
        if (startX < window.innerWidth - edgeThreshold) {
          touchStartX.current = 0;
          return;
        }
        // Don't hijack swipe on horizontally scrollable tabs or tables
        if (e.target.closest('.overflow-x-auto') || e.target.closest('table')) {
          touchStartX.current = 0;
          return;
        }
      }
      touchStartX.current = startX;
      touchStartY.current = e.changedTouches[0].screenY;
      swipeDetected.current = false;
    };

    const handleTouchMove = (e) => {
      if (!touchStartX.current || swipeDetected.current) return;
      const currentX = e.changedTouches[0].screenX;
      const currentY = e.changedTouches[0].screenY;
      const horizontalDiff = currentX - touchStartX.current;
      const verticalDiff = Math.abs(currentY - touchStartY.current);
      if (verticalDiff < verticalThreshold) {
        if (horizontalDiff < -swipeThreshold && !mobileMenuOpen) {
          setMobileMenuOpen(true);
          swipeDetected.current = true;
        } else if (horizontalDiff > swipeThreshold && mobileMenuOpen) {
          setMobileMenuOpen(false);
          swipeDetected.current = true;
        }
      }
    };

    const handleTouchEnd = () => {
      swipeDetected.current = false;
      touchStartX.current = 0;
    };

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [mobileMenuOpen, verticalThreshold, swipeThreshold]);

  // Cmd/Ctrl+K opens the command palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);


  // Cmd/Ctrl+B toggles the sidebar
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        toggleCollapsed();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toggleCollapsed]);


  const handleLogout = async () => {
    try {
      await logout();
      setMobileMenuOpen(false);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const hasRouteAccess = (route) => {
    if (!permissions) return false;
    if (!permissions.routes || permissions.routes.length === 0) return true;
    return permissions.routes.includes(route);
  };

  const hasCMSAccess = () => permissions && permissions.CMS_access === true;

  // Desktop sidebar nav items (no Home — logo handles that)
  const desktopNavItems = clientView ? [
    { to: `/portal/${clientView.id}`, label: 'Home', icon: HomeIcon, isActive: location.pathname.startsWith('/portal') },
    ...(permissions?.insightsAccess ? [{ to: `/insights?view=company&id=${clientView.id}`, label: 'Insights', icon: ChartBarIcon, isActive: location.pathname === '/insights' }] : []),
  ] : [
    ...(hasRouteAccess('/dashboard') ? [{ to: '/dashboard', label: 'Dashboard', icon: Squares2X2Icon, isActive: location.pathname === '/dashboard' }] : []),
    ...(hasRouteAccess('/insights') ? [{ to: '/insights', label: 'Insights', icon: ChartBarIcon, isActive: location.pathname === '/insights' }] : []),
    ...(hasRouteAccess('/clients') ? [{ to: '/clients', label: 'Clients', icon: UserCircleIcon, isActive: location.pathname.startsWith('/clients') }] : []),
    ...(hasCMSAccess() ? [{ to: '/cms', label: 'CMS', icon: PencilSquareIcon, isActive: location.pathname.startsWith('/cms') }] : []),
  ];

  // Mobile nav items (includes Home for non-clientView)
  const mobileNavItems = clientView ? desktopNavItems : [
    { to: '/', label: 'Home', isActive: location.pathname === '/' },
    ...desktopNavItems,
  ];

  const hasBanner = clientView && !isClientMode;
  const sidebarTop = hasBanner ? 'top-8' : 'top-0';
  const sidebarHeight = hasBanner ? 'h-[calc(100vh-2rem)]' : 'h-full';

  return (
    <>
      {/* Client view preview banner — only shown for consultant preview, not real client users */}
      {clientView && !isClientMode && (
        <div className="fixed top-0 left-0 w-full z-[70] bg-amber-500 text-amber-950 text-sm flex items-center justify-between px-4 py-1.5">
          <span>
            <span className="font-semibold">Previewing client portal:</span> {clientView.name}
          </span>
          <button
            onClick={exitClientView}
            className="font-semibold underline hover:no-underline ml-4"
          >
            Exit Preview
          </button>
        </div>
      )}

      {/* ─── Desktop Sidebar ──────────────────────────────────────── */}
      <aside
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`hidden md:flex flex-col fixed left-0 ${sidebarTop} ${sidebarHeight} z-[60]
          bg-header-bg-light/80 dark:bg-header-bg/80 backdrop-blur-md
          border-r border-border/10 dark:border-header-text/10
          transition-all duration-300 overflow-hidden
          ${(collapsed && !isHovered) ? 'w-16' : 'w-60'}
        `}
      >
        {/* Toggle + Logo */}
        <div className="flex items-center flex-shrink-0 h-16 border-b border-border/10 dark:border-header-text/10 px-2">
          <div className="flex items-center gap-3 px-3">
            <button
              onClick={toggleCollapsed}
              className="flex-shrink-0 hover:opacity-60 transition text-text-secondary-light dark:text-text-secondary"
              aria-label="Toggle sidebar"
            >
              {/* Panel-left sidebar icon */}
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 5.25A1.5 1.5 0 015.25 3.75h13.5A1.5 1.5 0 0120.25 5.25v13.5A1.5 1.5 0 0118.75 20.25H5.25a1.5 1.5 0 01-1.5-1.5V5.25z" />
                <path strokeLinecap="round" d="M9 3.75v16.5" />
              </svg>
            </button>
            {/* Logo: flex-shrink-0 so it never compresses; opacity fade only, sidebar overflow-hidden clips it */}
            <div
              className="flex-shrink-0"
              style={{ opacity: (collapsed && !isHovered) ? 0 : 1, transition: 'opacity 200ms ease', pointerEvents: (collapsed && !isHovered) ? 'none' : 'auto' }}
            >
              <Link to={clientView ? `/portal/${clientView.id}` : "/"} className="block hover:opacity-70 transition-opacity">
                <img
                  src={theme === "light" ? "/logolight.png" : "/logo.png"}
                  alt="SeyAI Insights Logo"
                  className="h-7 w-auto"
                />
              </Link>
            </div>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-3 space-y-0.5 px-2">
          {/* Command palette trigger */}
          <button
            onClick={() => setPaletteOpen(true)}
            title={(collapsed && !isHovered) ? 'Search (⌘K)' : undefined}
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-text-secondary-light dark:text-text-secondary hover:bg-black/5 dark:hover:bg-white/5 hover:text-text-primary-light dark:hover:text-text-primary overflow-x-hidden"
          >
            <MagnifyingGlassIcon className="w-5 h-5 flex-shrink-0" />
            <span
              className="whitespace-nowrap flex-shrink-0 flex items-center gap-2"
              style={{ opacity: (collapsed && !isHovered) ? 0 : 1, transition: 'opacity 200ms ease' }}
            >
              Search…
              <kbd className="text-xs bg-black/5 dark:bg-white/10 rounded px-1.5 py-0.5 font-sans">⌘K</kbd>
            </span>
          </button>

          {desktopNavItems.map(({ to, label, icon: Icon, isActive }) => (
            <Link
              key={to}
              to={to}
              title={(collapsed && !isHovered) ? label : undefined}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors overflow-hidden
                ${isActive
                  ? 'bg-brand-primary/10 text-brand-primary'
                  : 'text-text-secondary-light dark:text-text-secondary hover:bg-black/5 dark:hover:bg-white/5 hover:text-text-primary-light dark:hover:text-text-primary'
                }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span
                className="whitespace-nowrap flex-shrink-0"
                style={{ opacity: (collapsed && !isHovered) ? 0 : 1, transition: 'opacity 200ms ease' }}
              >{label}</span>
            </Link>
          ))}
        </nav>

        {/* Footer — avatar at same left offset as nav icons (px-2 outer + pl-3 inner = 20px);
             controls are flex-shrink-0 and fade out; sidebar overflow-hidden clips them */}
        <div className="flex-shrink-0 border-t border-border/10 dark:border-header-text/10 p-2">
          {!loading && currentUser && (
            <div className="flex items-center gap-2 pl-2 pr-1 py-1">
              {currentUser.photoURL && (
                <img
                  src={currentUser.photoURL}
                  alt={currentUser.displayName || "User"}
                  className="w-8 h-8 rounded-full flex-shrink-0"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              )}
              <div
                className="flex items-center gap-2 flex-shrink-0"
                style={{ opacity: (collapsed && !isHovered) ? 0 : 1, transition: 'opacity 200ms ease', pointerEvents: (collapsed && !isHovered) ? 'none' : 'auto' }}
              >
                <ThemeToggle />
                <Button variant="secondary" size="sm" onClick={handleLogout} className="whitespace-nowrap">
                  Logout
                </Button>
              </div>
            </div>
          )}
          {loading && (
            <div className="flex items-center gap-2 pl-3 pr-1 py-1">
              <div className="w-8 h-8 rounded-full bg-background-gray animate-pulse flex-shrink-0" />
              <div className="h-3 w-24 bg-background-gray rounded animate-pulse flex-shrink-0" />
            </div>
          )}
        </div>
      </aside>

      {/* ─── Mobile Top Header ────────────────────────────────────── */}
      <header
        className={`md:hidden bg-header-bg-light/60 dark:bg-header-bg/60 backdrop-blur-md text-header-text-light dark:text-header-text shadow-sm border-b border-border/10 dark:border-header-text/10 fixed left-0 w-full z-[60] transition duration-200 ${hasBanner ? 'top-8' : 'top-0'}`}
      >
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center overflow-x-hidden">
          <Link to={clientView ? `/portal/${clientView.id}` : "/"} className="text-2xl hover:opacity-70 transition">
            <img
              src={theme === "light" ? "/logolight.png" : "/logo.png"}
              alt="SeyAI Insights Logo"
              className="h-8 w-auto"
            />
          </Link>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg transition"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-5 flex flex-col justify-between">
              <span className={`block h-0.5 w-full bg-text-primary-light dark:bg-text-secondary transition-all duration-300 ${mobileMenuOpen ? "rotate-45 translate-y-[0.5625rem]" : ""}`} />
              <span className={`block h-0.5 w-full bg-text-primary-light dark:bg-text-secondary transition-all duration-50 ${mobileMenuOpen ? "opacity-0" : ""}`} />
              <span className={`block h-0.5 w-full bg-text-primary-light dark:bg-text-secondary transition-all duration-300 ${mobileMenuOpen ? "-rotate-45 -translate-y-[0.5625rem]" : ""}`} />
            </div>
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobileMenu}
      />

      {/* Mobile Menu Sidebar - Full Width */}
      <div
        className={`mobile-menu-sidebar fixed top-0 right-0 h-full w-full bg-header-bg-light dark:bg-header-bg z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Close Button */}
        <div className="flex justify-end px-4 py-4 border-b border-border-light dark:border-header-text/10">
          <button
            onClick={closeMobileMenu}
            className="p-2 hover:opacity-70 rounded-lg transition"
            aria-label="Close menu"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span className="block h-0.5 w-full bg-text-primary-light dark:bg-text-secondary rotate-45 translate-y-2" />
              <span className="block h-0.5 w-full bg-text-primary-light dark:bg-text-secondary -rotate-45 -translate-y-2" />
            </div>
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="flex flex-col h-[calc(100vh-65px)] p-6 overflow-y-auto">
          {/* User Profile */}
          {!loading && currentUser && (
            <div className="flex items-center gap-3 pb-6 mb-6 border-b border-border-light dark:border-header-text/20">
              {currentUser.photoURL && (
                <img
                  src={currentUser.photoURL}
                  alt={currentUser.displayName || "User"}
                  className="w-12 h-12 rounded-full"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              )}
              <div className="flex-1">
                <p className="text-base font-semibold text-header-text-light dark:text-header-text">
                  {currentUser.displayName || "User"}
                </p>
              </div>
              <ThemeToggle className="ml-4" />
              <Button variant="secondary" onClick={handleLogout}>Logout</Button>
            </div>
          )}

          {loading && (
            <div className="flex items-center gap-3 pb-6 mb-6 border-b border-header-text/20">
              <div className="w-12 h-12 rounded-full border-2 border-brand-primary bg-background-gray animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 bg-background-gray rounded animate-pulse" />
                <div className="h-3 w-40 bg-background-gray rounded animate-pulse" />
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <nav className="flex flex-col space-y-4 flex-1">
            {/* Search trigger */}
            <button
              onClick={() => { setPaletteOpen(true); setMobileMenuOpen(false); }}
              className="flex items-center gap-3 text-lg font-medium py-3 px-4 rounded-lg transition hover:opacity-70 text-text-primary-light dark:text-text-secondary"
            >
              <MagnifyingGlassIcon className="w-5 h-5 flex-shrink-0" />
              Search…
            </button>
            {mobileNavItems.map(({ to, label, isActive }) => (
              <Link
                key={to}
                to={to}
                onClick={closeMobileMenu}
                className={`text-lg font-medium py-3 px-4 rounded-lg transition ${
                  isActive
                    ? "bg-brand-primary/10 text-brand-primary"
                    : "hover:opacity-70 text-text-primary-light dark:text-text-secondary"
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Command Palette */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        permissions={permissions}
        allClients={allClients}
        clientView={clientView}
      />

      {/* Floating "View as Client" button — desktop only */}
      {isSeyTravelUser && !clientView && (
        <div className="hidden md:block fixed bottom-3 right-3 z-[60]" ref={clientDropdownRef}>
          <button
            onClick={() => setClientDropdownOpen((v) => !v)}
            className="flex items-center gap-1.5 text-sm text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary bg-white dark:bg-surface-dark border border-border-light dark:border-border rounded-xl px-4 py-2.5 shadow-lg transition-colors"
          >
            <EyeIcon className="w-4 h-4" />
            View as Client
            <ChevronDownIcon className={`w-3 h-3 transition-transform ${clientDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {clientDropdownOpen && (
            <div className="absolute right-0 bottom-full mb-2 w-64 bg-white dark:bg-surface-dark border border-border-light dark:border-border rounded-xl shadow-lg overflow-hidden">
              <div className="p-2 border-b border-border-light dark:border-border">
                <input
                  autoFocus
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search clients…"
                  className="w-full text-sm px-3 py-1.5 rounded-lg bg-background-light dark:bg-surface-dark border border-border-light dark:border-border text-text-primary-light dark:text-text-primary outline-none"
                />
              </div>
              <ul className="max-h-56 overflow-y-auto">
                {filteredClientsList.length === 0 ? (
                  <li className="px-3 py-3 text-sm text-text-muted-light dark:text-text-muted text-center">
                    {managedClients.length === 0 ? 'No managed clients yet' : 'No matches'}
                  </li>
                ) : filteredClientsList.map((c) => (
                  <li
                    key={c.id}
                    className="px-3 py-2.5 text-sm cursor-pointer hover:opacity-75 text-text-primary-light dark:text-text-primary transition"
                    onClick={() => {
                      setClientDropdownOpen(false);
                      setClientSearch('');
                      enterClientView(c);
                    }}
                  >
                    <span className="font-medium">{c.name}</span>
                    {c.industry && (
                      <span className="ml-2 text-text-muted-light dark:text-text-muted">{c.industry}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
}
